import NavigateNext from '@mui/icons-material/NavigateNext';
import { CardActions, Divider, IconButton, Stack, Typography } from '@mui/material';

import React, { ReactNode, type JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { WidgetMenu } from './WidgetMenu';
import { useTranslation } from 'react-i18next';

export const WidgetFooter = ({
    mode,
    ids,
    widgetData,
    disabled,
    modUrl,
    actionBtnName,
    noAction,
}: {
    mode: string | undefined;
    ids?: any;
    widgetData: any;
    disabled?: boolean;
    modUrl?: any;
    actionBtnName?: ReactNode;
    noAction?: boolean;
}): JSX.Element => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { deviceId, groupId, Id } = useParams();
    const footerHeight = mode?.includes('pinned-view') ? '40px' : '48px';
    const shouldOverlayFooter = Boolean(mode?.includes('view') || mode?.includes('pinned') || mode === 'edit');
    const route = widgetData?.route;
    const widgetTypeId = widgetData?.widgetTypeId ?? '';
    const widgetName = widgetData?.widgetName;
    const widgetId = widgetData?.id;
    const isMultiDevice = Boolean(widgetData?.multiDevice);
    const viewAllEnabled = Boolean(
        widgetData?.viewAllButton || widgetData?.displayViewBar || widgetData?.isViewAllDisplayed
    );

    const formatString = (input: string): string =>
        input
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

    const buildViewAllUrl = (singleLinkedDeviceId?: string): string => {
        const baseUrl = `${modUrl?.url ?? route?.url}${singleLinkedDeviceId ? `/${singleLinkedDeviceId}` : ''}`;
        return `${baseUrl}${route?.state ? `#${route.state}` : ''}`;
    };

    const footerName = (widgetTypeName: string): string => {
        if (widgetTypeName === 'energy_flow') {
            return 'Assets';
        } else if (widgetTypeName === 'timeline') {
            return 'Events';
        }
        return formatString(widgetTypeName) ?? '';
    };

    // For the Properties, Details and Settings linked widgets, when only one device is linked we
    // should show the chevron (">") navigation icon instead of rendering a dropdown menu with a
    // single entry. This behavior is intentionally scoped to `properties`, `details` and
    // `settings` widgets and is not applied to other (relative) widgets.
    const renderDropdownStack = (): JSX.Element => (
        <Stack
            direction={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
            width={'100%'}
            {...(shouldOverlayFooter && {
                sx: { position: 'absolute', bottom: '5px', left: '16px', width: '95%' },
            })}
        >
            <Typography
                variant={'subtitle2'}
                color={'primary'}
                sx={{ cursor: 'pointer' }}
                data-testid="view-all-footer"
            >
                {actionBtnName ?? `${t('COMMON:VIEW_ALL')} ${footerName(widgetTypeId)} `}
            </Typography>
            <WidgetMenu disabled={mode !== 'view' || disabled} widgetData={widgetData} ids={ids} />
        </Stack>
    );

    const renderNavigateStack = (isPropertiesWidget: boolean): JSX.Element => (
        <Stack
            direction={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
            width={'100%'}
            {...(shouldOverlayFooter && {
                sx: { position: 'absolute', bottom: '5px', left: '16px', width: '95%' },
            })}
            onClick={(): void => {
                if (route && mode === 'view' && !disabled) {
                    // When a single device is linked on a Properties/Details/Settings
                    // widget, navigate to that specific device's page (same target as the
                    // dropdown's menu item would use). Only the icon differs.
                    const singleLinkedDeviceId =
                        isPropertiesWidget && isMultiDevice && ids?.length === 1 ? ids[0]?.deviceId : undefined;
                    navigate(buildViewAllUrl(singleLinkedDeviceId), {
                        state: {
                            selectedTab: modUrl?.state ?? route?.state ?? '',
                            widgetName,
                            subTab: route?.subState ?? '',
                            widgetId,
                            deviceId: singleLinkedDeviceId ?? deviceId,
                            groupId: groupId ?? Id,
                        },
                    });
                }
            }}
        >
            <Typography variant={'subtitle2'} color={'primary'} data-testid="view-all-footer">
                {actionBtnName ?? `${t('COMMON:VIEW_ALL')} ${footerName(widgetTypeId)} `}
            </Typography>
            <IconButton aria-label="settings" disabled={mode !== 'view' || disabled}>
                <NavigateNext fontSize="medium" />
            </IconButton>
        </Stack>
    );

    const renderViewAllContent = (): JSX.Element | false => {
        const isPropertiesOrDetailsWidget =
            widgetTypeId === 'properties' || widgetTypeId === 'details' || widgetTypeId === 'settings';
        const shouldShowDeviceDropdown =
            isMultiDevice && ids?.length > 0 && !(isPropertiesOrDetailsWidget && ids?.length === 1);

        if (noAction || !viewAllEnabled) return false;

        return shouldShowDeviceDropdown ? renderDropdownStack() : renderNavigateStack(isPropertiesOrDetailsWidget);
    };

    return (
        (mode === 'edit' ||
            widgetData?.viewAllButton ||
            widgetData?.displayViewBar ||
            widgetData?.isViewAllDisplayed) && (
            <CardActions
                sx={{
                    height: footerHeight,
                    maxWidth: mode === 'edit' ? 'calc(100% - 45px)' : '100%',
                    position: shouldOverlayFooter ? 'absolute' : 'relative',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    ...(shouldOverlayFooter && { bottom: 0 }),
                    flexDirection: 'column',
                    // Remove the default CardActions 8px padding so the top divider spans edge-to-edge.
                    // The footer content is absolutely positioned (left: 16px) and is unaffected.
                    p: 0,
                    backgroundColor: '#fff',
                    ...(!noAction && {
                        cursor: mode !== 'view' || disabled ? 'auto' : 'pointer',
                        opacity: mode !== 'view' || disabled ? 0.5 : 1,
                    }),
                }}
            >
                <Divider sx={{ width: '100%', position: 'absolute', bottom: footerHeight }} />
                {renderViewAllContent()}
            </CardActions>
        )
    );
};
